// Онбординг (К-01, К-02, FR-ONB-01…03): что это → «тренер главнее» → согласие и профиль → сам / вместе.
// Без регистрации; первый сценарий — не позже 3 минут от запуска (UJ-1).
import { useState } from "react";
import { useNavigate } from "react-router";
import { ageOf, recommended, stageForAge } from "../lib/content/catalog.ts";
import { db, type Stage } from "../lib/db.ts";
import { upsertChild } from "../lib/events.ts";
import { enqueue, flush } from "../lib/pending.ts";
import { uuidv7 } from "../lib/uuid.ts";
import { api, reloadLocal, useApp } from "../state/app.ts";
import { t, years } from "../i18n/ru.ts";

const CONSENT_VERSION = "2026-10";

export function Onboarding() {
  const nav = useNavigate();
  const content = useApp((s) => s.content);
  const [step, setStep] = useState(0);
  const thisYear = new Date().getFullYear();
  const [nickname, setNickname] = useState("");
  const [birthYear, setBirthYear] = useState(thisYear - 5);
  const autoStage = stageForAge(ageOf(birthYear));
  const [stage, setStage] = useState<Stage | null>(null);
  const [format, setFormat] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const st = stage ?? autoStage;
  const stages = content?.dictionaries.stages ?? {};
  const formats = Object.values(content?.dictionaries.formats ?? {}).sort((a, b) => a.outfieldPlayers - b.outfieldPlayers || Number(a.goalkeeper) - Number(b.goalkeeper));
  const fmt = format ?? stages[st]?.defaultFormat ?? "1v1";

  const finish = async (mode: "self" | "together") => {
    const id = uuidv7();
    await upsertChild({ id, nickname: nickname.trim() || "Футболист", birthYear, stage: st, formatCode: fmt, updatedAt: Date.now() });
    await db.set("activeChildId", id);
    await db.set("onboarded", true);
    // Согласие — до создания профиля на сервере (FR-ONB-03); офлайн — отправится позже.
    await enqueue(db, "/v1/me/consents", { document: "child_data", version: CONSENT_VERSION });
    await enqueue(db, "/v1/me/consents", { document: "privacy", version: CONSENT_VERSION });
    void flush(api, db);
    await reloadLocal();
    useApp.setState({ onboarded: true });
    if (mode === "together") return nav("/kid", { replace: true });
    const rec = content ? recommended(content, st, []) : undefined;
    nav(rec ? `/scenario/${encodeURIComponent(rec.scenario.scenario.id)}` : "/today", { replace: true });
  };

  return (
    <div className="onboarding">
      {step === 0 ? (
        <section className="onb-card">
          <img src="/icons/icon.svg" alt="" width={96} height={96} />
          <h1>{t.onboarding.s1Title}</h1>
          <p>{t.onboarding.s1Text}</p>
          <button className="btn btn--primary btn--big" onClick={() => setStep(1)}>
            {t.common.next}
          </button>
        </section>
      ) : null}
      {step === 1 ? (
        <section className="onb-card">
          <h1>{t.onboarding.s2Title}</h1>
          <p>{t.onboarding.s2Text}</p>
          <button className="btn btn--primary btn--big" onClick={() => setStep(2)}>
            {t.onboarding.understood}
          </button>
        </section>
      ) : null}
      {step === 2 ? (
        <form
          className="onb-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (consent) setStep(3);
          }}
        >
          <h1>{t.onboarding.profileTitle}</h1>
          <label className="field">
            <span>{t.onboarding.nickname}</span>
            <input value={nickname} maxLength={40} onChange={(e) => setNickname(e.target.value)} autoComplete="off" />
          </label>
          <label className="field">
            <span>{t.onboarding.birthYear}</span>
            <select value={birthYear} onChange={(e) => setBirthYear(Number(e.target.value))}>
              {Array.from({ length: 14 }, (_, i) => thisYear - 3 - i).map((y) => (
                <option key={y} value={y}>
                  {y} ({years(thisYear - y)})
                </option>
              ))}
            </select>
          </label>
          <p className="muted">{t.onboarding.stageAuto(stages[autoStage]?.name ?? autoStage)}</p>
          <div className="row">
            <label className="field">
              <span>{t.onboarding.stage}</span>
              <select value={st} onChange={(e) => setStage(e.target.value as Stage)}>
                {Object.entries(stages).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.name} ({v.ages[0]}–{v.ages[1]})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t.onboarding.format}</span>
              <select value={fmt} onChange={(e) => setFormat(e.target.value)}>
                {formats.map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
            <span>{t.onboarding.consent}</span>
          </label>
          <p className="muted small">{t.onboarding.privacy}</p>
          <button className="btn btn--primary btn--big" disabled={!consent}>
            {t.onboarding.ready}
          </button>
        </form>
      ) : null}
      {step === 3 ? (
        <section className="onb-card">
          <h1>{t.onboarding.modeTitle}</h1>
          <div className="stack">
            <button className="btn btn--primary btn--big" onClick={() => void finish("together")}>
              {t.onboarding.together}
            </button>
            <button className="btn btn--big" onClick={() => void finish("self")}>
              {t.onboarding.self}
            </button>
          </div>
          <p className="muted small">{t.onboarding.install}</p>
        </section>
      ) : null}
    </div>
  );
}
